import { useEffect, useRef } from 'react';
import { useAuthStore } from '../../application/store/authStore';
import { apiClient } from '../../infrastructure/api/ApiClient';

/**
 * useSessionMonitor — sprint-130 S4 (sliding session).
 *
 * Mounted once under MineMinerLayout. On a timer it reads the live token expiry
 * from the auth store and:
 *   - while the rep is ACTIVE and the token is nearing expiry, proactively rotates
 *     it (silent refresh) so an actively-used app never expires mid-entry; and
 *   - while the rep is IDLE and near expiry, surfaces the SessionTimeoutWarning so
 *     they can explicitly extend or be logged out instead of silently lapsing.
 *
 * This is the proactive complement to the ApiClient's reactive 401-retry: the
 * retry guarantees no request (e.g. the booth submit) is ever lost, while this
 * keeps the session genuinely sliding. It is TTL-agnostic, so it keeps working
 * after the temporary access-token TTL band-aid is reverted.
 */
const CHECK_INTERVAL_MS = 30_000;
const PROACTIVE_REFRESH_LEAD_MS = 10 * 60_000; // refresh once <10 min remain
const WARNING_LEAD_MS = 5 * 60_000; // warn once <5 min remain (matches the modal's 300s bar)
const ACTIVITY_WINDOW_MS = 10 * 60_000; // "active" = interacted within the last 10 min

const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'mousedown',
  'keydown',
  'touchstart',
  'pointerdown',
  'scroll',
];

export function useSessionMonitor(): void {
  const lastActivityRef = useRef<number>(Date.now());
  const refreshingRef = useRef<boolean>(false);

  useEffect((): (() => void) => {
    const markActivity = (): void => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true }),
    );

    const tick = async (): Promise<void> => {
      const state = useAuthStore.getState();
      if (!state.isAuthenticated || state.tokenExpiresAt === null) {
        return;
      }

      const msRemaining = new Date(state.tokenExpiresAt).getTime() - Date.now();
      if (Number.isNaN(msRemaining)) {
        return;
      }

      const recentlyActive = Date.now() - lastActivityRef.current < ACTIVITY_WINDOW_MS;

      // Active + near expiry → slide the session silently.
      if (
        msRemaining > 0 &&
        msRemaining <= PROACTIVE_REFRESH_LEAD_MS &&
        recentlyActive &&
        !refreshingRef.current
      ) {
        refreshingRef.current = true;
        try {
          await apiClient.refreshSession();
        } finally {
          refreshingRef.current = false;
        }
        return;
      }

      // Idle + near expiry → let the rep decide via the warning modal.
      if (msRemaining <= WARNING_LEAD_MS && !recentlyActive && !state.sessionWarningShown) {
        state.showSessionWarning();
      }
    };

    const interval = window.setInterval((): void => {
      void tick();
    }, CHECK_INTERVAL_MS);
    void tick();

    return (): void => {
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActivity));
    };
  }, []);
}
